import { StoryViewer, type StoryViewerProfile } from '@/components/story-viewer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { type Profile } from '@/types';
import { Star } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function StoryRing({ profile }: { profile: Profile }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const stories = profile.stories ?? [];
  const hasStories = stories.length > 0;

  const avatar = (
    <Avatar className="h-28 w-28">
      <AvatarImage
        src={profile.avatar?.original_url}
        alt={`${profile.display_name} avatar`}
      />
      <AvatarFallback className="bg-muted text-2xl text-foreground">
        {profile.display_name
          .split(' ')
          .map((w) => w.charAt(0))
          .join('')
          .toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );

  if (!hasStories) return avatar;

  const viewerProfile: StoryViewerProfile = {
    id: profile.id!,
    slug: profile.slug,
    display_name: profile.display_name,
    avatar: profile.avatar,
    stories,
  };

  return (
    <>
      <button
        type="button"
        className="rounded-full bg-gradient-to-tr from-primary to-pink-400 p-0.5 focus:ring-2 focus:ring-primary focus:outline-none"
        onClick={() => setOpen(true)}
        aria-label={t('a11y.view_stories')}
      >
        <div className="rounded-full bg-background p-0.5">{avatar}</div>
      </button>
      <StoryViewer
        profile={viewerProfile}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function Header({ profile }: { profile: Profile }) {
  const { t } = useTranslation('common');

  return (
    <header
      className="rounded-lg border border-border bg-card p-4 sm:p-6"
      aria-labelledby="profile-heading"
    >
      {/* Top row: avatar + name/badges + rating */}
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          <StoryRing profile={profile} />
          {profile.is_available_now && (
            <div className="mt-1.5 flex items-center justify-center gap-1">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" />
              <span className="text-xs font-medium text-green-400">
                {t('profile.available_now')}
              </span>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1
              id="profile-heading"
              className="text-xl font-semibold sm:text-2xl"
            >
              {profile.display_name}
            </h1>
            {profile.verified && (
              <Badge variant="outline">{t('profile.verified')}</Badge>
            )}
            {profile.has_recent_review && profile.latest_review_at && (
              <Badge
                variant="outline"
                className="gap-1 border-green-500/40 text-xs text-green-400"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400" />
                {t('profile.new_badge')}
              </Badge>
            )}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {profile.neighborhood_name}
          </div>
        </div>

        {/* Rating — inline on mobile, same row */}
        {(profile?.rating?.count ?? 0) > 0 && (
          <div className="flex shrink-0 items-center gap-1.5">
            <Star className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-semibold">
              {profile?.rating?.fmt_rating}
            </span>
            <span className="hidden text-xs text-muted-foreground sm:inline">
              ({profile?.rating?.count})
            </span>
          </div>
        )}
      </div>

      {/* Description — below the row on all screen sizes */}
      {profile.description && (
        <div className="mt-3 text-sm text-muted-foreground">
          {profile.description}
        </div>
      )}
    </header>
  );
}
